import React, { useEffect, useMemo, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import Mention from "@tiptap/extension-mention";
import TextAlign from "@tiptap/extension-text-align";

/**
 * props:
 *  - value: string (HTML)
 *  - onChange: (html: string) => void
 *  - placeholder?: string
 *  - users?: Array<{ id: string|number, value?: string, name?: string, email?: string, avatar?: string }>
 *  - minHeight?: number (px)
 *  - className?: string
 */
export default function TiptapEditor({
  value = "",
  onChange,
  placeholder = "Escreva aqui…",
  users = [],
  minHeight = 50,
  className = "",
}) {
  // mapeia usuários de forma tolerante a formatos
  const mappedUsers = useMemo(() => {
    const arr = Array.isArray(users) ? users : [];
    return arr
      .map((u) => {
        const rawId =
          u?.id ?? u?.userId ?? u?._id ?? u?.value ?? u?.email ?? u?.username;
        if (!rawId) return null;
        const label =
          u?.name ??
          u?.fullName ??
          u?.displayName ??
          u?.value ??
          u?.label ??
          u?.email ??
          u?.username ??
          "Usuário";
        const email = Array.isArray(u?.emails)
          ? u.emails[0] || ""
          : u?.email || u?.primaryEmail || "";
        const avatar =
          u?.avatar ||
          u?.avatarUrl ||
          u?.photoURL ||
          u?.photoUrl ||
          u?.picture ||
          "";
        return {
          id: String(rawId),
          label: String(label),
          email: String(email || ""),
          avatar: String(avatar || ""),
        };
      })
      .filter(Boolean);
  }, [users]);

  // ref dinâmico para o suggestion.items
  const usersRef = useRef([]);
  useEffect(() => {
    usersRef.current = mappedUsers;
  }, [mappedUsers]);

  const extensions = useMemo(() => {
    return [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        history: { depth: 100, newGroupDelay: 500 },
        bulletList: { keepMarks: true, keepAttributes: true },
        orderedList: { keepMarks: true, keepAttributes: true },
        codeBlock: true,
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Link.configure({
        openOnClick: true,
        autolink: true,
        linkOnPaste: true,
      }),
      Placeholder.configure({ placeholder }),
      Mention.extend({
        name: "mention",
        inline: true,
        group: "inline",
        atom: true,
        addAttributes() {
          return {
            id: { default: null },
            label: { default: null },
            email: { default: null },
            avatar: { default: null },
          };
        },
        renderHTML({ HTMLAttributes }) {
          // gera um span com data-* para possível parsing server-side
          return [
            "span",
            {
              "data-type": "mention",
              "data-id": HTMLAttributes.id || "",
              "data-label": HTMLAttributes.label || "",
              "data-email": HTMLAttributes.email || "",
              class: "tt-mention",
            },
            `@${HTMLAttributes.label || "usuário"}`,
          ];
        },
      }).configure({
        suggestion: {
          char: "@",
          items: ({ query }) => {
            const base = usersRef.current || [];
            const q = (query || "").toLowerCase();
            return base
              .filter((it) => !q || it.label.toLowerCase().includes(q))
              .slice(0, 8);
          },
          render: () => {
            let popup = null;

            const place = (el, rectFn) => {
              const rect = rectFn?.();
              if (!rect) return;
              Object.assign(el.style, {
                position: "fixed",
                left: `${rect.left}px`,
                top: `${rect.bottom + 8}px`,
                zIndex: 9999,
              });
            };

            const update = (root, props) => {
              root.innerHTML = "";
              const base = usersRef.current || [];

              if (!base.length) {
                root.textContent = "Nenhum usuário disponível";
                return;
              }
              if (!props.items?.length) {
                root.textContent = props.query
                  ? "Sem resultados"
                  : "Digite para buscar…";
                return;
              }

              props.items.forEach((it, idx) => {
                const row = document.createElement("button");
                row.type = "button";
                row.className =
                  "tt-item flex w-full items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 text-left";
                row.style.cursor = "pointer";

                const avatar = document.createElement("div");
                avatar.className =
                  "w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden text-xxs";
                if (it.avatar) {
                  const img = document.createElement("img");
                  img.src = it.avatar;
                  img.alt = it.label;
                  img.width = 24;
                  img.height = 24;
                  avatar.appendChild(img);
                } else {
                  avatar.textContent =
                    it.label?.slice(0, 2)?.toUpperCase() || "U";
                }

                const text = document.createElement("div");
                text.className = "min-w-0";
                text.innerHTML = `
                  <div class="text-[13px] font-medium truncate">${
                    it.label
                  }</div>
                  ${
                    it.email
                      ? `<div class="text-[11px] text-gray-500 truncate">${it.email}</div>`
                      : ""
                  }
                `;

                row.appendChild(avatar);
                row.appendChild(text);
                row.addEventListener("mousedown", (e) => {
                  e.preventDefault();
                  props.command({
                    id: it.id,
                    label: it.label,
                    email: it.email,
                    avatar: it.avatar,
                  });
                });

                root.appendChild(row);
                if (idx === 0) row.classList.add("bg-gray-50");
              });
            };

            return {
              onStart: (props) => {
                const el = document.createElement("div");
                el.className =
                  "tt-suggest shadow-lg border rounded-md bg-white p-1 text-sm";
                update(el, props);
                popup = el;
                document.body.appendChild(popup);
                place(popup, props.clientRect);
              },
              onUpdate(props) {
                if (!popup) return;
                update(popup, props);
                place(popup, props.clientRect);
              },
              onKeyDown(props) {
                if (props.event.key === "Escape") {
                  popup?.remove();
                  popup = null;
                  return true;
                }
                return false;
              },
              onExit() {
                popup?.remove();
                popup = null;
              },
            };
          },
        },
      }),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeholder]); // não dependa de mappedUsers aqui

  const editor = useEditor({
    extensions,
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "tt-editor max-w-none focus:outline-none", // sem .prose para não depender do plugin
      },
    },
  });

  // sincroniza quando value muda "de fora"
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  // UI helpers
  const Sep = () => <span className="tt-sep" aria-hidden="true" />;

  const Btn = ({ active, disabled, onClick, title, children }) => (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={!!active}
      className={`tt-btn ${active ? "tt-btn--active" : ""}`}
    >
      {children}
    </button>
  );

  // Dropdown de títulos
  const HeadingSelect = () => {
    const isP = editor.isActive("paragraph");
    const h1 = editor.isActive("heading", { level: 1 });
    const h2 = editor.isActive("heading", { level: 2 });
    const h3 = editor.isActive("heading", { level: 3 });

    const val = h1 ? "h1" : h2 ? "h2" : h3 ? "h3" : "p";
    const change = (e) => {
      const v = e.target.value;
      const chain = editor.chain().focus();
      if (v === "p") chain.setParagraph().run();
      if (v === "h1") chain.toggleHeading({ level: 1 }).run();
      if (v === "h2") chain.toggleHeading({ level: 2 }).run();
      if (v === "h3") chain.toggleHeading({ level: 3 }).run();
    };

    return (
      <select
        className="tt-select"
        value={val}
        onChange={change}
        title="Estilo do texto"
      >
        <option value="p">Parágrafo</option>
        <option value="h1">Título 1</option>
        <option value="h2">Título 2</option>
        <option value="h3">Título 3</option>
      </select>
    );
  };

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL do link:", prev || "https://");
    if (url === null) return; // cancel
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    try {
      const safe = new URL(url).toString();
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: safe })
        .run();
    } catch {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: `https://${url}` })
        .run();
    }
  };

  const clearFormatting = () => {
    editor.chain().focus().clearNodes().unsetAllMarks().run();
  };

  return (
    <div className={`tt-wrapper ${className}`}>
      {/* Toolbar (sticky, alto contraste) */}
      <div className="tt-toolbar">
        <HeadingSelect />

        <Sep />

        <Btn
          title="Negrito (Ctrl/Cmd+B)"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <b>B</b>
        </Btn>
        <Btn
          title="Itálico (Ctrl/Cmd+I)"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <i>I</i>
        </Btn>
        <Btn
          title="Tachado"
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <span style={{ textDecoration: "line-through" }}>S</span>
        </Btn>
        <Btn
          title="Bloco de código"
          active={editor.isActive("codeBlock")}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        >
          {"{ }"}
        </Btn>

        <Sep />

        <Btn
          title="Lista com marcadores"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          • Lista
        </Btn>
        <Btn
          title="Lista numerada"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          1. Lista
        </Btn>
        <Btn
          title="Citação"
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          “”
        </Btn>
        <Sep />

        <Btn title="Link" active={editor.isActive("link")} onClick={setLink}>
          🔗
        </Btn>
        {editor.isActive("link") && (
          <Btn
            title="Remover link"
            onClick={() => editor.chain().focus().unsetLink().run()}
          >
            ❌
          </Btn>
        )}

        <Sep />

        <Btn
          title="Desfazer (Ctrl/Cmd+Z)"
          onClick={() => editor.chain().focus().undo().run()}
        >
          ⟲
        </Btn>
        <Btn
          title="Refazer (Shift+Ctrl/Cmd+Z)"
          onClick={() => editor.chain().focus().redo().run()}
        >
          ⟳
        </Btn>
      </div>

      {/* Editor */}
      <div className="tt-editor-wrap">
        <EditorContent editor={editor} />
      </div>

      {/* estilos */}
      <style>{`
        .tt-wrapper {
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          background: #fff;
          overflow:hidden;
        }

        .tt-toolbar {
          position: sticky;
          top: 0;
          z-index: 5;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 6px;
          padding: 10px;
          border-bottom: 1px solid #e5e7eb;
          background:rgb(255, 255, 255);
        }

        .tt-select {
          height: 34px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          background: #fff;
          padding: 0 10px;
          font-size: 14px;
        }

        .tt-sep {
          width: 1px;
          height: 24px;
          background: #d1d5db;
          margin: 0 2px;
        }

        .tt-btn {
          height: 34px;
          padding: 0 10px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          background: #fff;
          font-size: 14px;
          line-height: 1;
          cursor: pointer;
        }
        .tt-btn:hover { background: #f3f4f6; }
        .tt-btn--active {
          background: #111827;
          color: #fff;
          border-color: #111827;
        }
        .tt-btn:disabled {
          opacity: .5;
          cursor: not-allowed;
        }

        .tt-editor-wrap .tt-editor {
          min-height: ${minHeight}px;
          padding: 14px 16px;
          font-size: 14.5px;
          line-height: 1.55;
          color: #111827;
        }

        /* ---- Conteúdo tipográfico (sem depender do Tailwind Typography) ---- */
        .tt-editor h1 { font-size: 1.45rem; font-weight: 700; margin: .5rem 0 .6rem; }
        .tt-editor h2 { font-size: 1.25rem; font-weight: 700; margin: .5rem 0 .55rem; }
        .tt-editor h3 { font-size: 1.1rem;  font-weight: 700; margin: .45rem 0 .5rem; }

        .tt-editor p { margin: .3rem 0; }
        .tt-editor p.is-empty { margin: .15rem 0; }
        .tt-editor a { text-decoration: underline; color: #1d4ed8; }

        .tt-editor ul, .tt-editor ol { margin: .4rem 0 .4rem 0; padding-left: 1.25rem; }
        .tt-editor ul { list-style: disc outside; }
        .tt-editor ol { list-style: decimal outside; }
        .tt-editor li { margin: .2rem 0; }

        .tt-editor blockquote {
          border-left: 3px solid #e5e7eb;
          margin: .6rem 0;
          padding: .35rem .8rem;
          color: #4b5563;
          background: #f9fafb;
          border-radius: 6px;
        }

        .tt-editor code {
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 1px 6px;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.92em;
        }
        .tt-editor pre {
          background: #0b1020;
          color: #f3f4f6;
          border-radius: 10px;
          padding: 10px 12px;
          overflow: auto;
          border: 1px solid #111827;
        }
        .tt-editor pre code {
          background: transparent;
          border: 0;
          padding: 0;
          color: inherit;
          font-size: 0.95em;
        }

        .tt-mention {
          background: #eef2ff;
          color:#3730a3;
          padding: 2px 6px;
          border-radius: 6px;
          white-space: nowrap;
        }

        /* popup de menções */
        .tt-suggest { max-width: 320px; }
        .tt-item:focus { outline: none; }

        /* popup de menções */
.tt-suggest {
  max-width: 320px;
  /* 👇 novo: rolagem quando tiver muitos itens */
  max-height: 260px;
  overflow-y: auto;
  overscroll-behavior: contain; /* evita “puxar” o scroll da página */
  scrollbar-gutter: stable;     /* mantém layout estável com scrollbar */
}

/* scrollbar “suave” (opcional) */
.tt-suggest::-webkit-scrollbar { width: 8px; }
.tt-suggest::-webkit-scrollbar-thumb { background:#d1d5db; border-radius: 9999px; }
.tt-suggest::-webkit-scrollbar-track { background: transparent; }

      `}</style>
    </div>
  );
}
